import { ApiRoutes } from "../../../Constants/ApiRoutingConstants";
import axios, { AxiosInstance } from "axios";
import authorizeService from "../../Identity/Utilities/AuthorizeService";
import AssetTemplateListItemDto from "../../../Models/Assets/AssetTemplateListItemDto";
import AssetTemplateReadDto from "../../../Models/Assets/AssetTemplateReadDto";
import AssetImageReadDto from "../../../Models/Assets/AssetImageReadDto";

/**
 * API service for asset templates (CRUD, list, image).
 * Can be used from Administration, Site Setup, or any feature that manages templates.
 */
class TemplatesApiService {
  axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: process.env.REACT_APP_DEVICE_MANAGEMENT_SERVICE_URL,
    });
  }

  /** GET web/v1/templates/assets - Specific site. */
  async getTemplatesAssets(): Promise<AssetTemplateListItemDto[]> {
    let response = await this.axiosInstance({
      method: "GET",
      url: ApiRoutes.TemplatesAssetsV1,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data as AssetTemplateListItemDto[];
  }

  /** GET web/v1/templates/assets/all - All templates */
  async getTemplatesAssetsAll(): Promise<AssetTemplateListItemDto[]> {
    let response = await this.axiosInstance({
      method: "GET",
      url: ApiRoutes.TemplatesAssetsAllV1,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data as AssetTemplateListItemDto[];
  }

  /** GET web/v1/templates/assets/{id} - get one asset template by id */
  async getTemplateAsset(id: string): Promise<AssetTemplateReadDto> {
    let response = await this.axiosInstance({
      method: "GET",
      url: `${ApiRoutes.TemplatesAssetsV1}/${id}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data as AssetTemplateReadDto;
  }

  /** GET web/v1/images/{imageId} - get image by id (used to load template image when editing) */
  async getImageById(imageId: string): Promise<AssetImageReadDto> {
    let response = await this.axiosInstance({
      method: "GET",
      url: `${ApiRoutes.ImagesV1}/${imageId}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data as AssetImageReadDto;
  }

  /** POST web/v1/templates/assets - create asset template */
  async createTemplateAsset(payload: any) {
    let response = await this.axiosInstance({
      method: "POST",
      url: ApiRoutes.TemplatesAssetsV1,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
      data: payload,
    });
    return response.data;
  }

  /** PUT web/v1/templates/assets/{id} - update asset template */
  async updateTemplateAsset(id: string, payload: any) {
    let response = await this.axiosInstance({
      method: "PUT",
      url: `${ApiRoutes.TemplatesAssetsV1}/${id}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
      data: payload,
    });
    return response.data;
  }

  /** DELETE web/v1/templates/assets/{id} - delete asset template */
  async deleteTemplateAsset(id: string) {
    let response = await this.axiosInstance({
      method: "DELETE",
      url: `${ApiRoutes.TemplatesAssetsV1}/${id}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data;
  }

  /** POST web/v1/assets/bulk-update - Bulk update assets when template changes. */
  async bulkUpdateAssets(payload: { templateId: string; assetIds?: string[] }) {
    let response = await this.axiosInstance({
      method: "POST",
      url: `${ApiRoutes.AssetsV1}/bulk-update`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
      data: payload,
    });
    return response.data;
  }
}

const templatesApiService = new TemplatesApiService();
export default templatesApiService;
